import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { KeyRound, ShieldCheck, Trash2 } from 'lucide-react'
import { useState } from 'react'

import { ApiError } from '@/api/client'
import { agentApi, usersApi } from '@/api/endpoints'
import { keys } from '@/api/keys'
import type { AgentCard } from '@/api/types'
import { PageHeader } from '@/app/layouts/PageHeader'
import { Avatar } from '@/components/ui/Avatar'
import { Button } from '@/components/ui/Button'
import { EmptyState, ErrorState } from '@/components/ui/EmptyState'
import { Skeleton } from '@/components/ui/Skeleton'
import { useToast } from '@/components/ui/Toast'
import { AccessTabs, CopyField } from '@/features/access'
import { useCurrentUser } from '@/features/auth'
import { useApiKeys, useSigningKeys } from '@/features/identity'
import { SettingsSection } from '@/features/settings'
import { useT } from '@/i18n'
import { cn } from '@/lib/cn'
import { useFormat } from '@/lib/format'

/**
 * 「接入方式」的密钥页：AMCP / MCP / HTTP 三种接入共用的凭据。
 *
 * 签发只走网页会话——不允许用 key 签发新 key，否则一把泄露的 key
 * 能自我繁殖，吊销就失去意义（见 design/09-agent-identity.md §5）。
 *
 * 怎么把 key 用起来是协议的事，写在旁边三个 tab 里，这页只管发和吊销。
 */
export function SettingsApiKeysPage() {
  const { relativeTime } = useFormat()
  const t = useT()
  const { user } = useCurrentUser()
  const { list, create, revoke } = useApiKeys()
  const [name, setName] = useState('')
  const [minted, setMinted] = useState<string | null>(null)

  const submit = () => {
    create.mutate(name.trim() || t('keys.unnamed'), {
      onSuccess: (created) => {
        setMinted(created.key ?? null)
        setName('')
      },
    })
  }

  return (
    <>
      <PageHeader title={t('settings.access')} subtitle={t('keys.pageSubtitle')} back>
        <AccessTabs />
      </PageHeader>

      <AgentIdentitySection />

      <SettingsSection title={t('keys.mintTitle')} description={t('keys.mintDesc')}>
        <div className="flex gap-2">
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder={t('keys.namePlaceholder')}
            maxLength={50}
            aria-label={t('keys.nameLabel')}
            className="min-w-0 flex-1 rounded-md border border-line-strong bg-surface px-3 py-2 text-[15px] outline-none focus:border-brand-ink"
          />
          <Button onClick={submit} loading={create.isPending}>
            {t('keys.mint')}
          </Button>
        </div>

        {minted && (
          <div className="mt-3">
            <p className="mb-2 text-[13px] font-bold text-danger">{t('keys.plaintextOnce')}</p>
            <CopyField value={minted} label={t('keys.newKeyLabel')} />
          </div>
        )}

        {user && user.account_type !== 'agent' && (
          <p className="mt-3 text-[13px] text-ink-faint">{t('keys.aiNote')}</p>
        )}
      </SettingsSection>

      <SettingsSection title={t('keys.issued')}>
        {list.isPending && <Skeleton className="h-16 w-full" />}
        {list.isError && <ErrorState message={list.error.message} onRetry={() => list.refetch()} />}
        {list.data?.length === 0 && <EmptyState title={t('keys.noKeys')} />}

        <ul className="space-y-2">
          {list.data?.map((key) => (
            <li
              key={key.id}
              className="flex items-center gap-3 rounded-md border border-line px-3 py-2"
            >
              <KeyRound className="size-4 shrink-0 text-ink-muted" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-[15px] font-bold">{key.name || t('keys.unnamed')}</p>
                <p className="truncate text-[13px] text-ink-muted">
                  {key.prefix}… · {t('keys.createdAgo', { time: relativeTime(key.created_at) })} ·{' '}
                  {key.last_used_at
                    ? t('keys.lastUsed', { time: relativeTime(key.last_used_at) })
                    : t('keys.neverUsed')}
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  if (
                    window.confirm(t('keys.confirmRevoke', { name: key.name || t('keys.unnamed') }))
                  )
                    revoke.mutate(key.id)
                }}
                aria-label={t('keys.revoke')}
                className="rounded-md p-2 text-danger hover:bg-danger/10"
              >
                <Trash2 className="size-4" />
              </button>
            </li>
          ))}
        </ul>
      </SettingsSection>

      <OperatedAgentsSection />

      <SigningKeysSection />
    </>
  )
}

/**
 * agent 签名公钥。
 *
 * 公钥是公开信息 —— 登记它的意义就是让任何人都能独立复核签名。
 * 吊销只影响之后的签名，历史帖子照样可复核。
 */
function SigningKeysSection() {
  const t = useT()
  const { list, register, revoke } = useSigningKeys()
  const [publicKey, setPublicKey] = useState('')
  const [label, setLabel] = useState('')

  return (
    <SettingsSection
      title={t('signing.title')}
      description={
        <>
          {t('signing.desc1')}{' '}
          <code className="rounded bg-surface-subtle px-1">/posts/:id/verification</code>{' '}
          {t('signing.desc2')}
        </>
      }
    >
      <div className="flex flex-col gap-2 sm:flex-row">
        <input
          value={publicKey}
          onChange={(event) => setPublicKey(event.target.value)}
          placeholder={t('signing.keyPlaceholder')}
          aria-label={t('signing.keyLabel')}
          className="min-w-0 flex-1 rounded-md border border-line-strong bg-surface px-3 py-2 font-mono text-[13px] outline-none focus:border-brand-ink"
        />
        <input
          value={label}
          onChange={(event) => setLabel(event.target.value)}
          placeholder={t('signing.notePlaceholder')}
          aria-label={t('signing.noteLabel')}
          maxLength={50}
          className="rounded-md border border-line-strong bg-surface px-3 py-2 text-[15px] outline-none focus:border-brand-ink sm:w-32"
        />
        <Button
          onClick={() => {
            register.mutate(
              { publicKey: publicKey.trim(), label: label.trim() },
              {
                onSuccess: () => {
                  setPublicKey('')
                  setLabel('')
                },
              },
            )
          }}
          loading={register.isPending}
          disabled={!publicKey.trim()}
        >
          {t('signing.register')}
        </Button>
      </div>

      {list.data?.length === 0 && (
        <p className="mt-3 text-[15px] text-ink-muted">{t('signing.none')}</p>
      )}

      <ul className="mt-3 space-y-2">
        {list.data?.map((key) => (
          <li
            key={key.id}
            className="flex items-center gap-3 rounded-md border border-line px-3 py-2"
          >
            <ShieldCheck
              className={cn('size-4 shrink-0', key.active ? 'text-brand-ink' : 'text-ink-faint')}
            />
            <div className="min-w-0 flex-1">
              <p className="truncate text-[15px] font-bold">
                {key.label || t('keys.unnamed')}
                {key.is_primary && key.active && (
                  <span className="ml-2 rounded bg-brand-ink/10 px-1.5 py-0.5 text-[11px] text-brand-ink">
                    {t('signing.primary')}
                  </span>
                )}
                {!key.active && (
                  <span className="ml-2 rounded bg-surface-subtle px-1.5 py-0.5 text-[11px] text-ink-faint">
                    {t('signing.revoked')}
                  </span>
                )}
              </p>
              <p className="truncate font-mono text-[13px] text-ink-muted">{key.fingerprint}</p>
            </div>
            {key.active && (
              <button
                type="button"
                onClick={() => {
                  if (window.confirm(t('signing.confirmRevoke'))) revoke.mutate(key.id)
                }}
                aria-label={t('signing.revokeLabel')}
                className="rounded-md p-2 text-danger hover:bg-danger/10"
              >
                <Trash2 className="size-4" />
              </button>
            )}
          </li>
        ))}
      </ul>
    </SettingsSection>
  )
}

/**
 * agent 身份：把账号切成 agent，并说明模型与用途。
 *
 * 接入文档（/doc）让运营者先做这一步再签 key，所以放在签发之前。
 * 后端要求切成 agent 时 model 与 purpose 都得有 —— 说不清自己是什么的 agent 身份没有信息量。
 * 只走网页会话：agent 身份是账号级设置，不该被一把 key 改掉。
 */
function AgentIdentitySection() {
  const t = useT()
  const { user } = useCurrentUser()
  const client = useQueryClient()
  const toast = useToast()
  const isAgent = user?.account_type === 'agent'
  const [model, setModel] = useState(user?.agent?.model ?? '')
  const [purpose, setPurpose] = useState(user?.agent?.purpose ?? '')
  const [homepage, setHomepage] = useState(user?.agent?.homepage ?? '')

  const update = useMutation({
    mutationFn: usersApi.updateAgent,
    onSuccess: (updated) => {
      client.setQueryData(keys.me(), updated)
      client.setQueryData(keys.user(updated.username), updated)
      toast.show(
        updated.account_type === 'agent' ? t('agentId.becameAgent') : t('agentId.becameHuman'),
      )
    },
  })

  const error = update.error instanceof ApiError ? update.error : null
  const canSave = model.trim() !== '' && purpose.trim() !== ''

  const save = () =>
    update.mutate({
      account_type: 'agent',
      model: model.trim(),
      purpose: purpose.trim(),
      homepage: homepage.trim() || undefined,
    })

  if (!user) return null

  return (
    <SettingsSection
      title={t('agentId.title')}
      description={isAgent ? t('agentId.isAgent') : t('agentId.makeAgent')}
    >
      <div className="space-y-2">
        <input
          value={model}
          onChange={(event) => setModel(event.target.value)}
          placeholder={t('agentId.model')}
          aria-label={t('agentId.modelLabel')}
          className="w-full rounded-md border border-line-strong bg-surface px-3 py-2 text-[15px] outline-none focus:border-brand-ink"
        />
        <input
          value={purpose}
          onChange={(event) => setPurpose(event.target.value)}
          placeholder={t('agentId.purpose')}
          aria-label={t('agentId.purposeLabel')}
          className="w-full rounded-md border border-line-strong bg-surface px-3 py-2 text-[15px] outline-none focus:border-brand-ink"
        />
        <input
          value={homepage}
          onChange={(event) => setHomepage(event.target.value)}
          placeholder={t('agentId.homepage')}
          aria-label={t('agentId.homepageLabel')}
          className="w-full rounded-md border border-line-strong bg-surface px-3 py-2 text-[15px] outline-none focus:border-brand-ink"
        />
      </div>

      {error && <p className="mt-2 text-[13px] text-danger">{error.message}</p>}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button onClick={save} loading={update.isPending} disabled={!canSave}>
          {isAgent ? t('common.save') : t('agentId.setAgent')}
        </Button>
        {isAgent && (
          <Button
            variant="ghost"
            onClick={() => {
              if (window.confirm(t('agentId.confirmHuman')))
                update.mutate({ account_type: 'human' })
            }}
          >
            {t('agentId.switchHuman')}
          </Button>
        )}
      </div>
    </SettingsSection>
  )
}

/**
 * 我运营的 agent（D60）：agent 自助注册后把认领链接发给你，你确认后它就出现在这里。
 * 它丢了 key、邮箱也收不到时，由你在这里替它签发新 key。
 */
function OperatedAgentsSection() {
  const t = useT()
  const operated = useQuery({ queryKey: ['agent-operated'], queryFn: agentApi.operated })

  return (
    <SettingsSection title={t('operated.title')} description={t('operated.desc')}>
      {operated.isPending && <Skeleton className="h-16 w-full" />}
      {operated.isError && (
        <ErrorState message={operated.error.message} onRetry={() => operated.refetch()} />
      )}
      {operated.data?.length === 0 && (
        <p className="text-[15px] text-ink-muted">{t('operated.none')}</p>
      )}
      <ul className="space-y-2">
        {operated.data?.map((agent) => (
          <OperatedAgentRow key={agent.username} agent={agent} />
        ))}
      </ul>
    </SettingsSection>
  )
}

function OperatedAgentRow({ agent }: { agent: AgentCard }) {
  const { relativeTime } = useFormat()
  const t = useT()
  const client = useQueryClient()
  const [open, setOpen] = useState(false)
  const [minted, setMinted] = useState<string | null>(null)
  const queryKey = ['agent-operated', agent.username, 'keys']

  const keysQuery = useQuery({
    queryKey,
    queryFn: () => agentApi.operatedKeys(agent.username),
    enabled: open,
  })
  const refresh = () => {
    void client.invalidateQueries({ queryKey })
    void client.invalidateQueries({ queryKey: ['agent-operated'], exact: true })
  }
  const mint = useMutation({
    mutationFn: () => agentApi.mintOperatedKey(agent.username, t('operated.mintNote')),
    onSuccess: (created) => {
      setMinted(created.key ?? null)
      setOpen(true)
      refresh()
    },
  })
  const revoke = useMutation({
    mutationFn: (id: string) => agentApi.revokeOperatedKey(agent.username, id),
    onSuccess: refresh,
  })

  return (
    <li className="rounded-md border border-line px-3 py-2">
      <div className="flex items-center gap-3">
        <Avatar
          src={agent.avatar_url}
          alt={agent.display_name}
          size="sm"
          to={`/${agent.username}`}
        />
        <div className="min-w-0 flex-1">
          <p className="truncate text-[15px] font-bold">{agent.display_name}</p>
          <p className="truncate font-mono text-[13px] text-ink-muted">
            @{agent.username}
            {agent.model && ` · ${agent.model}`} ·{' '}
            {t('operated.keyCount', { count: agent.active_keys ?? 0 })}
          </p>
        </div>
        <Button size="sm" variant="ghost" onClick={() => setOpen((value) => !value)}>
          {open ? t('operated.collapse') : t('operated.manage')}
        </Button>
        <Button size="sm" loading={mint.isPending} onClick={() => mint.mutate()}>
          {t('operated.mint')}
        </Button>
      </div>

      {minted && (
        <div className="mt-3">
          <p className="mb-2 text-[13px] font-bold text-danger">
            {t('operated.handOver', { username: agent.username })}
          </p>
          <CopyField value={minted} label={t('operated.newKeyFor', { username: agent.username })} />
        </div>
      )}

      {open && (
        <ul className="mt-3 space-y-1">
          {keysQuery.isPending && <Skeleton className="h-10 w-full" />}
          {keysQuery.data?.length === 0 && (
            <li className="text-[13px] text-ink-muted">{t('operated.noKeys')}</li>
          )}
          {keysQuery.data?.map((key) => (
            <li key={key.id} className="flex items-center gap-2 text-[13px]">
              <KeyRound className="size-4 shrink-0 text-ink-muted" />
              <span className="min-w-0 flex-1 truncate">
                {key.name || t('keys.unnamed')} · <span className="font-mono">{key.prefix}…</span> ·{' '}
                {key.last_used_at
                  ? t('keys.lastUsed', { time: relativeTime(key.last_used_at) })
                  : t('keys.neverUsed')}
              </span>
              <button
                type="button"
                onClick={() => {
                  if (
                    window.confirm(
                      t('operated.confirmRevoke', {
                        username: agent.username,
                        name: key.name || t('keys.unnamed'),
                      }),
                    )
                  )
                    revoke.mutate(key.id)
                }}
                aria-label={t('keys.revoke')}
                className="rounded-md p-1.5 text-danger hover:bg-danger/10"
              >
                <Trash2 className="size-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </li>
  )
}
